import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'antd';
import { appToast } from '@utils/appToast';
import { handleShowToastError } from '@utils/helpers';
import { sealRequestApi } from '@services/sealRequestApi';
import { assetApi } from '@services/assetApi';
import { layoutApi } from '@services/layoutApi';
import { inventorySessionApi } from '@services/inventorySessionApi';
import {
  COMMON_ERR_NETWORK,
  COMMON_MESSAGE_ERROR,
  OPTION_EMPTY,
} from '@constants/common';
import { TSelectValue } from '@models/common';
import { TInventorySessionItem } from '@models/asset';
import { TSealRequestListReq } from '@models/sealRequest';
import type { Key } from 'react';
import _ from 'lodash';
import {
  FORM_NAME,
  MANAGEMENT_SEAL_REQUEST_CONSTANT,
  QUANTITY_MAX,
  QUANTITY_MIN,
  SEND_REQUEST_STATUS_CODE,
  TSealRequestRow,
} from '../constant';

const { MESSAGE } = MANAGEMENT_SEAL_REQUEST_CONSTANT;

const SUCCESS_TOAST_DURATION = 2;

function isQuantityInvalid(value: number | null): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (!Number.isInteger(value)) {
    return true;
  }
  return value < QUANTITY_MIN || value > QUANTITY_MAX;
}

function isKnownHttpOrNetworkError(error: any): boolean {
  const hasHttpStatus =
    Boolean(error) && typeof error === 'object' && 'status' in error;

  return hasHttpStatus || error === 'ERR_NETWORK' || error === 'ERR_TIMEOUT';
}

function sortSealRequestRows(rows: TSealRequestRow[]): TSealRequestRow[] {
  return [...rows].sort((left, right) => {
    const compareStoreCode = (left.storeCode || '').localeCompare(
      right.storeCode || ''
    );
    if (compareStoreCode !== 0) {
      return compareStoreCode;
    }

    return (left.assetNo || '').localeCompare(right.assetNo || '');
  });
}

function useManagementSealRequest() {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [rows, setRows] = useState<TSealRequestRow[]>([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingOther, setLoadingOther] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const [companiesSelect, setCompaniesSelect] = useState<TSelectValue[]>([]);
  const [storesSelect, setStoresSelect] = useState<TSelectValue[]>([]);
  const [sessionsSelect, setSessionsSelect] = useState<TInventorySessionItem[]>(
    []
  );
  const [filters, setFilters] = useState<TSealRequestListReq>({});

  const [selectedRow, setSelectedRow] = useState<TSealRequestRow | null>(null);
  const [quantityEditOpen, setQuantityEditOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [newQuantity, setNewQuantity] = useState<number | null>(null);
  const [quantityError, setQuantityError] = useState('');
  const [loadingConfirm, setLoadingConfirm] = useState(false);

  const sortedSealRequests = useMemo(() => sortSealRequestRows(rows), [rows]);

  const loadSealRequests = useCallback(
    async (payload?: TSealRequestListReq) => {
      setLoadingPage(true);
      try {
        const response = await sealRequestApi.getSealRequests(payload);
        if (response.ok) {
          const data = response.data || [];
          setRows(data);
          // checkbox selection UI tạm ẩn — không cần default-select-all nữa.
          // setSelectedRowKeys(data.map((item) => item.id));
        } else {
          setRows([]);
          appToast.error({ message: MESSAGE.LOAD_ERROR }); // MSG-PC008-E01
        }
      } catch (error) {
        setRows([]);
        // Network failure → generic message; everything else → MSG-PC008-E01. Never both (would
        // double up the toast).
        appToast.error({
          message:
            error === COMMON_ERR_NETWORK
              ? COMMON_MESSAGE_ERROR
              : MESSAGE.LOAD_ERROR,
        });
      } finally {
        setLoadingPage(false);
      }
    },
    []
  );

  useEffect(() => {
    const init = async () => {
      setLoadingOther(true);
      // 会社 [4.1] dropdown source aligned with PC004 (GET /api/v1/assets/lookups) rather than
      // the master company list — same raw t_assets.CompanyCode/CompanyName values PC004 uses.
      let defaultSessionCode: string | undefined;
      try {
        const [lookupsRes, storesRes, sessionsRes] = await Promise.all([
          assetApi.getAssetLookups(),
          layoutApi.getStoresByCompany({ companyCode: '', companyName: '' }),
          inventorySessionApi.getSessions(),
        ]);

        if (lookupsRes.ok) {
          setCompaniesSelect([
            OPTION_EMPTY,
            ...lookupsRes.data.companies.map((val) => ({
              value: val.companyCode,
              label: `${val.companyCode}_${val.companyName}`,
            })),
          ]);
        }

        if (storesRes.ok) {
          setStoresSelect([
            OPTION_EMPTY,
            ...storesRes.data.map((val) => ({
              value: val.id,
              label: `${val.storeCode}_${val.storeName}`,
            })),
          ]);
        }

        if (sessionsRes.ok) {
          const sessions = _.chain(sessionsRes.data)
            .uniqBy((item) => item.sessionCode)
            .orderBy((item) => item.inventoryYearMonth, 'desc')
            .value();
          setSessionsSelect(sessions);
          // 年月 default = kỳ gần nhất, giống PC004 (useAllDevice.tsx: sessionsSelect[0] sau khi sort desc).
          defaultSessionCode = sessions[0]?.sessionCode;
          if (defaultSessionCode) {
            form.setFieldValue(FORM_NAME.SESSION_CODE, defaultSessionCode);
          }
        }
      } catch (error) {
        handleShowToastError(error);
      } finally {
        setLoadingOther(false);
      }

      const initialFilters: TSealRequestListReq = defaultSessionCode
        ? { sessionCode: defaultSessionCode }
        : {};
      setFilters(initialFilters);
      loadSealRequests(initialFilters);
    };

    init();
  }, [loadSealRequests, form]);

  const handleNavigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSearch = useCallback(
    (values: TSealRequestListReq) => {
      const params: TSealRequestListReq = {
        sessionCode: values.sessionCode || undefined,
        companyCode: values.companyCode || undefined,
        storeId: values.storeId || undefined,
      };
      setFilters(params);
      loadSealRequests(params);
    },
    [loadSealRequests]
  );

  const handleRowSelectionChange = useCallback((keys: Key[]) => {
    setSelectedRowKeys(keys);
  }, []);

  const handleExportData = useCallback(async () => {
    setLoadingExport(true);
    try {
      // checkbox selection UI (Ids) tạm ẩn — export luôn áp dụng cho toàn bộ dữ liệu
      // đang hiển thị theo filters hiện tại (không gửi `ids`).
      const response = await sealRequestApi.exportSealRequests(filters);
      if (response.ok) {
        appToast.success({
          message: MESSAGE.EXPORT_SUCCESS,
          duration: SUCCESS_TOAST_DURATION,
        });
      } else {
        appToast.error({ message: MESSAGE.EXPORT_ERROR });
      }
    } catch (error) {
      // Same reasoning as loadSealRequests — one message only, no extra generic toast.
      appToast.error({
        message:
          error === COMMON_ERR_NETWORK
            ? COMMON_MESSAGE_ERROR
            : MESSAGE.EXPORT_ERROR,
      });
    } finally {
      setLoadingExport(false);
    }
  }, [filters]);

  const handleOpenQuantityEdit = useCallback((record: TSealRequestRow) => {
    setSelectedRow(record);
    setNewQuantity(record.quantity ?? 0);
    setQuantityError('');
    setQuantityEditOpen(true);
  }, []);

  const handleCloseQuantityEdit = useCallback(() => {
    setQuantityEditOpen(false);
    setSelectedRow(null);
    setNewQuantity(null);
    setQuantityError('');
  }, []);

  const handleChangeQuantity = useCallback(
    (value: number | null) => {
      setNewQuantity(value);
      if (quantityError) {
        setQuantityError('');
      }
    },
    [quantityError]
  );

  const validateQuantity = useCallback((value: number | null): boolean => {
    if (isQuantityInvalid(value)) {
      setQuantityError(MESSAGE.QUANTITY_REQUIRED); // MSG-PC008-V01
      return false;
    }

    setQuantityError('');
    return true;
  }, []);

  const handleSubmitQuantity = useCallback(() => {
    if (!validateQuantity(newQuantity)) {
      return;
    }
    setSendConfirmOpen(true);
  }, [newQuantity, validateQuantity]);

  const handleCloseSendConfirm = useCallback(() => {
    // [E4]/[E1.1] — back to Dialog D, keep D6 input as-is (do not reset selectedRow/newQuantity).
    setSendConfirmOpen(false);
  }, []);

  const handleConfirmSend = useCallback(async () => {
    if (!selectedRow || newQuantity === null || loadingConfirm) {
      // Guards double-click [E5] idempotency (TC-PC008-026): while a request is in-flight,
      // subsequent clicks are no-ops.
      return;
    }

    setLoadingConfirm(true);
    try {
      const response = await sealRequestApi.confirmSealRequest(
        selectedRow.requestCode,
        {
          quantity: newQuantity,
          requestStatusCode: SEND_REQUEST_STATUS_CODE, // always explicit "SENT" — see models/sealRequest.ts
        }
      );

      if (response.ok) {
        const confirmedRequestCode = selectedRow.requestCode;
        const confirmedStatus =
          response.data?.requestStatusCode ?? SEND_REQUEST_STATUS_CODE;

        setRows((prev) =>
          newQuantity === 0
            ? prev.filter((e) => e.requestCode !== confirmedRequestCode)
            : prev.map((row) =>
                row.requestCode === confirmedRequestCode
                  ? {
                      ...row,
                      quantity: newQuantity,
                      requestStatusCode: confirmedStatus,
                    }
                  : row
              )
        );
        if (newQuantity === 0) {
          // checkbox selection UI tạm ẩn — selectedRowKeys không còn được set từ UI, nhưng
          // giữ filter này để không vỡ khi bật lại tính năng.
          setSelectedRowKeys((prev) =>
            prev.filter((key) => key !== selectedRow.id)
          );
        }
        appToast.success({
          message: MESSAGE.SEND_SUCCESS, // MSG-PC008-S01
          duration: SUCCESS_TOAST_DURATION,
        });
        setSendConfirmOpen(false);
        setQuantityEditOpen(false);
        setSelectedRow(null);
        setNewQuantity(null);
      } else {
        // Business failure (ok:false) — MSG-PC008-E02. Per the literal reading of spec/TC (see
        // api-integration.md analyzing-fe Open Item #3 / TC-PC008-042/050/054): both dialogs close,
        // returning to VAMS_PC_008 main screen (not back to Dialog D) — unconfirmed with BA yet.
        appToast.error({ message: MESSAGE.SEND_ERROR });
        setSendConfirmOpen(false);
        setQuantityEditOpen(false);
        setSelectedRow(null);
        setNewQuantity(null);
      }
    } catch (error: any) {
      // Known HTTP failures (4xx/5xx incl. 404) and network/timeout rejections from
      // `axiosInstance` → MSG-PC008-E02; anything else unexpected → MSG-PC008-E03.
      //
      // NOTE (Open Item — api-integration.md §4 does not give a discriminating error code
      // between MSG-E02 "business/API failure" and MSG-E03 "unhandled system exception"; both
      // can surface as HTTP 500 from `ApiExceptionFilterAttribute`). This maps all recognizable
      // HTTP/network errors to E02 and reserves E03 for genuinely unexpected shapes — flagging
      // for BA/BE confirmation rather than guessing a stricter rule silently.
      if (isKnownHttpOrNetworkError(error)) {
        appToast.error({ message: MESSAGE.SEND_ERROR }); // MSG-PC008-E02
        handleShowToastError(error);
      } else {
        appToast.error({ message: MESSAGE.SYSTEM_ERROR }); // MSG-PC008-E03
      }
      // Same literal-reading behavior as the ok:false branch above — close both dialogs.
      setSendConfirmOpen(false);
      setQuantityEditOpen(false);
      setSelectedRow(null);
      setNewQuantity(null);
    } finally {
      setLoadingConfirm(false);
    }
  }, [selectedRow, newQuantity, loadingConfirm]);

  return {
    state: {
      form,
      rows: sortedSealRequests,
      loadingPage,
      loadingOther,
      loadingExport,
      companiesSelect,
      storesSelect,
      sessionsSelect,
      selectedRow,
      selectedRowKeys,
      quantityEditOpen,
      sendConfirmOpen,
      newQuantity,
      quantityError,
      loadingConfirm,
    },
    handler: {
      handleNavigateBack,
      handleSearch,
      handleExportData,
      handleRowSelectionChange,
      handleOpenQuantityEdit,
      handleCloseQuantityEdit,
      handleChangeQuantity,
      handleSubmitQuantity,
      handleCloseSendConfirm,
      handleConfirmSend,
    },
  };
}

export default useManagementSealRequest;
